import { useState, useEffect, useCallback, useRef } from "react";
import type { Post } from "@/types";
import { track } from "@/lib/log";

interface UsePostExposureOptions {
    posts: Post[];
    channel?: string;
    basePath: string;
    threshold?: number; // 曝光阈值，默认0.5
    rootMargin?: string; // Observer的rootMargin，默认'0px'
}

interface UsePostExposureReturn {
    exposedPosts: Set<string>;
    registerPostRef: (element: HTMLDivElement | null, postId: string) => void;
    resetExposure: () => void; // 重置曝光状态的方法
}

/**
 * PostCard曝光检测hooks
 * 
 * 使用IntersectionObserver自动检测PostCard进入视口并记录曝光事件
 * 支持防重复曝光、自动清理、配置曝光阈值等功能
 * 
 * @param options 配置选项
 * @param options.posts 帖子列表数组
 * @param options.channel 频道标识，用于区分不同的内容源
 * @param options.basePath 基础路径，用于标识页面类型
 * @param options.threshold 曝光阈值（0-1），默认0.5表示50%进入视口时触发
 * @param options.rootMargin Observer的rootMargin，默认'0px'
 * @returns 曝光状态和操作方法
 */
export function usePostExposure({
    posts,
    channel,
    basePath,
    threshold = 0.5,
    rootMargin = '0px'
}: UsePostExposureOptions): UsePostExposureReturn {
    // 曝光检测相关状态
    const [exposedPosts, setExposedPosts] = useState<Set<string>>(new Set());
    const postRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const exposureObserver = useRef<IntersectionObserver | null>(null);

    // 处理PostCard曝光
    const handlePostExposure = useCallback((post: Post) => {
        if (exposedPosts.has(post.doc_id)) return; // 已经曝光过，不重复记录

        setExposedPosts(prev => new Set([...prev, post.doc_id]));

        // 记录曝光事件
        track("post_exposure", {
            action: "view",
            doc_id: post.doc_id,
            type: "exposure",
            page: "list",
            channel: channel,
            base_path: basePath,
            author_id: post.author?.id,
            post_title: post.title,
            position: posts.findIndex(p => p.doc_id === post.doc_id), // 在列表中的位置
            total_posts: posts.length, // 当前列表总数
            timestamp: Date.now(), // 曝光时间戳
            threshold: threshold, // 曝光阈值
            viewport_info: {
                width: window.innerWidth,
                height: window.innerHeight
            }
        });

        // console.log(`📊 PostCard曝光: ${post.title} (ID: ${post.doc_id})`);
    }, [exposedPosts, channel, basePath, posts, threshold]);

    // 初始化曝光检测Observer
    useEffect(() => {
        exposureObserver.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const postId = entry.target.getAttribute('data-post-id');
                        const post = posts.find(p => p.doc_id === postId);
                        if (post) {
                            handlePostExposure(post);
                        }
                    }
                });
            },
            {
                root: null,
                rootMargin,
                threshold, // 可配置的曝光阈值
            }
        );

        return () => {
            if (exposureObserver.current) {
                exposureObserver.current.disconnect();
            }
        };
    }, [handlePostExposure, threshold, rootMargin]);

    // 为PostCard注册曝光检测
    const registerPostRef = useCallback((element: HTMLDivElement | null, postId: string) => {
        if (element) {
            postRefs.current.set(postId, element);
            if (exposureObserver.current) {
                exposureObserver.current.observe(element);
            }
        } else {
            // 清理
            const oldElement = postRefs.current.get(postId);
            if (oldElement && exposureObserver.current) {
                exposureObserver.current.unobserve(oldElement);
            }
            postRefs.current.delete(postId);
        }
    }, []);

    // 重置曝光状态（用于刷新数据时）
    const resetExposure = useCallback(() => {
        setExposedPosts(new Set());
        // 清理所有现有的Observer
        if (exposureObserver.current) {
            exposureObserver.current.disconnect();
        }
        postRefs.current.clear();

        console.log('🔄 已重置曝光状态');
    }, []);

    // 当posts数组发生变化时，清理不存在的post引用
    useEffect(() => {
        const currentPostIds = new Set(posts.map(p => p.doc_id));

        // 清理已经不存在的posts的曝光状态
        setExposedPosts(prev => {
            const newExposed = new Set<string>();
            prev.forEach(id => {
                if (currentPostIds.has(id)) {
                    newExposed.add(id);
                }
            });
            return newExposed;
        });

        // 清理已经不存在的post的引用
        for (const [postId, element] of postRefs.current.entries()) {
            if (!currentPostIds.has(postId)) {
                if (exposureObserver.current) {
                    exposureObserver.current.unobserve(element);
                }
                postRefs.current.delete(postId);
            }
        }
    }, [posts]);

    return {
        exposedPosts,
        registerPostRef,
        resetExposure
    };
}

export default usePostExposure; 